import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import "./styles/Attendance.css";

const backendUrl = "https://sokonishushi.co.tz/api";

export default function Attendance() {
    const [user, setUser] = useState(null);
    const [schedules, setSchedules] = useState([]);
    const [todaySchedule, setTodaySchedule] = useState(null);
    const [attendanceStatus, setAttendanceStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showCamera, setShowCamera] = useState(false);
    const [currentAction, setCurrentAction] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const videoRef = useRef();
    const canvasRef = useRef();
    const streamRef = useRef(null);

    const token = localStorage.getItem("token");

    useEffect(() => {
        const userData = {
            names: localStorage.getItem("names") || localStorage.getItem("name"),
            role: localStorage.getItem("role"),
            staff_id: localStorage.getItem("staff_id"),
            phonenumber: localStorage.getItem("phonenumber")
        };
        setUser(userData);
        fetchSchedules();
        
        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
        };
    }, []);

    const fetchSchedules = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/get_staff_all_schedules.php`, {
                headers: { "Authorization": `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success && data.schedules) {
                setSchedules(data.schedules);
                
                const today = new Date().toISOString().split('T')[0];
                const todaysSchedule = data.schedules.find(s => s.shift_date === today);
                setTodaySchedule(todaysSchedule || null);
                setAttendanceStatus(todaysSchedule?.attendance_status || 'pending');
            }
        } catch (error) {
            console.error("Error fetching schedules:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load schedules',
                confirmButtonColor: '#6B5B95',
                timer: 2000
            });
        } finally {
            setLoading(false);
        }
    };

    const startCamera = async (action) => {
        setCurrentAction(action);
        setShowCamera(true);
        
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.play();
            }
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Camera Error',
                text: 'Please allow camera access to continue.',
                confirmButtonColor: '#6B5B95',
                timer: 2000
            });
            setShowCamera(false);
        }
    };

    const captureAndSubmit = async () => {
        if (!videoRef.current) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Camera not ready',
                confirmButtonColor: '#6B5B95',
                timer: 1500
            });
            return;
        }

        Swal.fire({
            title: 'Processing...',
            text: 'Capturing your information...',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        try {
            const ctx = canvasRef.current.getContext("2d");
            ctx.drawImage(videoRef.current, 0, 0, 300, 225);
            const selfie = canvasRef.current.toDataURL("image/jpeg");
            
            let position = { latitude: 0, longitude: 0 };
            if (navigator.geolocation) {
                await new Promise((resolve) => {
                    navigator.geolocation.getCurrentPosition(
                        (pos) => {
                            position = {
                                latitude: pos.coords.latitude,
                                longitude: pos.coords.longitude
                            };
                            resolve();
                        },
                        () => resolve()
                    );
                });
            }
            
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
                streamRef.current = null;
            }
            setShowCamera(false);
            
            const payload = {
                action: currentAction,
                schedule_id: todaySchedule?.id,
                selfie: selfie,
                latitude: position.latitude,
                longitude: position.longitude
            };
            
            const response = await fetch(`${backendUrl}/attendance.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });
            
            const data = await response.json();
            
            Swal.close();
            
            if (data.success) {
                setAttendanceStatus(currentAction === 'sign_in' ? 'signed_in' : 'signed_out');
                Swal.fire({
                    icon: 'success',
                    title: `${currentAction === 'sign_in' ? 'Signed In' : 'Signed Out'} Successfully!`,
                    html: `Time: ${data.time}<br>Date: ${data.date}`,
                    confirmButtonColor: '#6B5B95',
                    timer: 2500,
                    showConfirmButton: false
                });
                fetchSchedules();
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Failed',
                    text: data.error || 'Please try again.',
                    confirmButtonColor: '#6B5B95',
                    timer: 2000
                });
            }
        } catch (err) {
            Swal.close();
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Network error. Please try again.',
                confirmButtonColor: '#6B5B95',
                timer: 2000
            });
        }
    };

    const handleSignIn = () => {
        if (!todaySchedule) {
            Swal.fire({
                icon: 'info',
                title: 'No Schedule Today',
                text: 'You don\'t have a schedule assigned for today.',
                confirmButtonColor: '#6B5B95',
                timer: 2000
            });
            return;
        }
        startCamera('sign_in');
    };

    const handleSignOut = () => {
        if (!todaySchedule) {
            Swal.fire({
                icon: 'info',
                title: 'No Schedule Today',
                text: 'You don\'t have a schedule assigned for today.',
                confirmButtonColor: '#6B5B95',
                timer: 2000
            });
            return;
        }
        startCamera('sign_out');
    };

    // Logout without confirmation - just click on name
    const handleLogout = () => {
        localStorage.clear();
        window.location.href = "/";
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    if (loading) {
        return (
            <div className="attendance-loading">
                <div className="spinner"></div>
                <p>Loading your schedule...</p>
            </div>
        );
    }

    return (
        <div className="attendance-app">
            {/* Mobile Menu Button */}
            <button className="mobile-menu-btn" onClick={toggleSidebar}>
                ☰
            </button>

            {/* Overlay for mobile */}
            {sidebarOpen && (
                <div className="sidebar-overlay" onClick={toggleSidebar}></div>
            )}

            {/* Sidebar */}
            <aside className={`attendance-sidebar ${sidebarOpen ? 'open' : ''}`}>
                <div className="sidebar-brand">
                    <h2>AttendFlow</h2>
                    <button className="close-sidebar" onClick={toggleSidebar}>×</button>
                </div>
                
                {/* Click on user profile to logout - NO CONFIRMATION */}
                <div className="user-profile" onClick={handleLogout} style={{ cursor: 'pointer' }}>
                    <div className="user-avatar">
                        {user?.names?.charAt(0) || 'S'}
                    </div>
                    <div className="user-details">
                        <span className="user-fullname">{user?.names || 'Staff'}</span>
                        <span className="user-badge staff">Staff • Click to logout</span>
                    </div>
                </div>
                
                <nav className="sidebar-menu">
                    <Link to="/staff-attendance" className="menu-item active">
                        <span>📋</span> Attendance
                    </Link>
                    <button onClick={handleLogout} className="menu-item logout">
                        <span>🚪</span> Logout
                    </button>
                </nav>
            </aside>

            {/* Main Content */}
            <main className="attendance-main-area">
                <div className="page-header">
                    <h1>My Attendance</h1>
                    <p>Track your daily check-ins and check-outs</p>
                </div>

                {/* Today's Schedule Card */}
                <div className="today-card">
                    <div className="today-badge">Today's Schedule</div>
                    {todaySchedule ? (
                        <div className="schedule-display">
                            <div className="schedule-date">
                                <span className="date-day">{todaySchedule.day}</span>
                                <span className="date-full">{formatDate(todaySchedule.shift_date)}</span>
                            </div>
                            
                            {attendanceStatus === 'pending' && (
                                <button className="action-btn signin-btn" onClick={handleSignIn}>
                                    Sign In
                                </button>
                            )}
                            {attendanceStatus === 'signed_in' && (
                                <button className="action-btn signout-btn" onClick={handleSignOut}>
                                    Sign Out
                                </button>
                            )}
                            {attendanceStatus === 'signed_out' && (
                                <div className="completed-status">
                                    ✓ Completed
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="no-schedule-message">
                            <p>No schedule assigned for today</p>
                            <span>Please contact your administrator</span>
                        </div>
                    )}
                </div>

                {/* Upcoming Schedules List */}
                <div className="upcoming-section">
                    <h3>Upcoming Schedules</h3>
                    <div className="schedules-list">
                        {schedules && schedules.filter(s => s.shift_date > new Date().toISOString().split('T')[0]).length > 0 ? (
                            schedules
                                .filter(s => s.shift_date > new Date().toISOString().split('T')[0])
                                .map((schedule) => (
                                    <div key={schedule.id} className="schedule-item">
                                        <div className="schedule-date-info">
                                            <span className="schedule-day">{schedule.day}</span>
                                            <span className="schedule-date">{formatDate(schedule.shift_date)}</span>
                                        </div>
                                        <div className="schedule-status pending">
                                            Upcoming
                                        </div>
                                    </div>
                                ))
                        ) : (
                            <div className="empty-list">
                                <p>No upcoming schedules</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Camera Modal */}
                {showCamera && (
                    <div className="camera-modal">
                        <div className="camera-container">
                            <div className="camera-header">
                                <h3>Face Verification</h3>
                                <button className="close-camera" onClick={() => {
                                    if (streamRef.current) {
                                        streamRef.current.getTracks().forEach(track => track.stop());
                                    }
                                    setShowCamera(false);
                                }}>×</button>
                            </div>
                            <div className="camera-preview">
                                <video ref={videoRef} autoPlay playsInline className="camera-feed"></video>
                                <canvas ref={canvasRef} style={{ display: "none" }} width="300" height="225"></canvas>
                            </div>
                            <div className="camera-actions">
                                <button className="capture-btn" onClick={captureAndSubmit}>
                                    Capture & Submit
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}